/* splashHost — mounts SplashMaze into its own React root, OUTSIDE the app
   tree, so it can paint before msal.initialize()/handleRedirectPromise()
   while the app root keeps its mandatory post-redirect mount ordering
   (main.tsx:69-74). Owns teardown so the .5s splashFadeOut actually plays
   (in-tree unmount used to batch it away). */

import { createRoot, type Root } from 'react-dom/client';
import { SplashMaze } from './SplashMaze';
import { readSplashPlan } from './splashPlan';

const HOST_ID = 'daedalus-splash';
let host: { el: HTMLDivElement; root: Root } | null = null;

export function mountSplash(): void {
  if (host || document.getElementById(HOST_ID)) return; // idempotent
  const el = document.createElement('div');
  el.id = HOST_ID;
  document.body.appendChild(el);
  const root = createRoot(el);
  host = { el, root };
  const plan = readSplashPlan();
  root.render(
    <SplashMaze
      minMs={plan.minMs}
      maxMs={plan.maxMs}
      quick={plan.quick}
      onDone={() => {
        // data-done CSS fade (.5s) plays over the already-painted app.
        setTimeout(unmountSplash, 600);
      }}
    />
  );
}

export function unmountSplash(): void {
  if (!host) return;
  const { el, root } = host;
  host = null;
  root.unmount();
  el.remove();
}

/** boot() failure surface — swap the animation for a static message. */
export function showBootError(): void {
  const el =
    host?.el ?? (document.getElementById(HOST_ID) as HTMLDivElement | null);
  if (host) { host.root.unmount(); host = null; }
  const target = el ?? document.body.appendChild(document.createElement('div'));
  target.id = HOST_ID;
  target.innerHTML =
    '<div style="position:fixed;inset:0;z-index:200;display:flex;align-items:center;' +
    'justify-content:center;flex-direction:column;gap:12px;background:var(--bg-1,#141415);' +
    'color:var(--ink-2,#9a9aa2);font-family:\'JetBrains Mono\',monospace;font-size:12px;' +
    'letter-spacing:.14em;text-transform:uppercase">' +
    '<div>daedalus failed to start</div>' +
    '<button onclick="location.reload()" style="padding:8px 16px;background:transparent;' +
    'color:inherit;border:1px solid currentColor;cursor:pointer;font:inherit">reload</button></div>';
}
